import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Inbox as InboxIcon, Mail, Phone, MapPin, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Inbox() {
  const [token, setToken] = useState(() => localStorage.getItem("sm_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [unread, setUnread] = useState(0);
  const [fetching, setFetching] = useState(false);

  const load = async (tk) => {
    setFetching(true);
    try {
      const h = { headers: { Authorization: `Bearer ${tk}` } };
      const [list, state] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, h),
        axios.get(`${API}/admin/inbox/state`, h),
      ]);
      setInquiries(list.data);
      setUnread(state.data.unread);
      axios.post(`${API}/admin/inbox/seen`, {}, h).catch(() => {});
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        toast.error("Failed to load inquiries.");
      }
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => { if (token) load(token); }, []); // eslint-disable-line

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("sm_admin_token", data.token);
      setToken(data.token);
      setPassword("");
      load(data.token);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("sm_admin_token");
    setToken("");
    setInquiries([]);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm rounded-2xl border border-brand-border bg-brand-card p-8 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-brand-gold text-white"><InboxIcon size={18} /></span>
            <div>
              <div className="font-display text-xl text-brand-fg">Enquiry Inbox</div>
              <div className="text-xs uppercase tracking-[0.2em] text-brand-gold">Sunrise Mart</div>
            </div>
          </div>
          <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="mt-6 w-full rounded-xl border border-brand-border bg-brand-soft px-4 py-3 text-sm text-brand-fg focus:border-brand-gold focus:outline-none" />
          <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="mt-3 w-full rounded-xl border border-brand-border bg-brand-soft px-4 py-3 text-sm text-brand-fg focus:border-brand-gold focus:outline-none" />
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-brand-gold px-6 py-3 text-sm font-semibold text-white hover:bg-brand-gold/90 transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={16} /> : null} Sign in
          </button>
          <a href="/" className="mt-4 block text-center text-xs text-brand-muted hover:text-brand-gold">← Back to website</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      <header className="sticky top-0 z-10 border-b border-brand-border bg-brand-bg/80 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="relative grid place-items-center h-9 w-9 rounded-full bg-brand-gold text-white">
              <InboxIcon size={16} />
              {unread > 0 && (
                <span data-testid="unread-badge" className="absolute -top-1.5 -right-1.5 grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold">{unread}</span>
              )}
            </span>
            <div>
              <div className="font-display text-lg text-brand-fg">Enquiry Inbox</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">{inquiries.length} enquiries{unread > 0 ? ` · ${unread} new` : ""}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="refresh-btn" onClick={() => load(token)} className="grid place-items-center h-9 w-9 rounded-full border border-brand-border text-brand-fg/70 hover:text-brand-gold hover:border-brand-gold transition-colors"><RefreshCw size={15} className={fetching ? "animate-spin" : ""} /></button>
            <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 rounded-full border border-brand-border px-4 py-2 text-sm text-brand-fg/70 hover:text-brand-gold hover:border-brand-gold transition-colors"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-10">
        {fetching && inquiries.length === 0 ? (
          <div className="flex justify-center py-20 text-brand-muted"><Loader2 className="animate-spin" /></div>
        ) : inquiries.length === 0 ? (
          <div className="text-center py-20 text-brand-muted">No enquiries yet.</div>
        ) : (
          <div className="space-y-4" data-testid="inquiry-list">
            {inquiries.map((q) => (
              <div key={q.id} data-testid="inquiry-row" className="rounded-2xl border border-brand-border bg-brand-card p-6 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-xl text-brand-fg">{q.name}{q.company ? <span className="text-brand-muted text-base"> · {q.company}</span> : null}</div>
                    <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-brand-fg/70">
                      <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 hover:text-brand-gold"><Mail size={13} /> {q.email}</a>
                      {q.phone ? <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 hover:text-brand-gold"><Phone size={13} /> {q.phone}</a> : null}
                      {q.country ? <span className="flex items-center gap-1.5"><MapPin size={13} /> {q.country}</span> : null}
                    </div>
                  </div>
                  <div className="text-right">
                    {q.product ? <span className="inline-block rounded-full bg-brand-goldSoft px-3 py-1 text-xs font-medium text-brand-gold">{q.product}</span> : null}
                    <div className="mt-1 text-xs text-brand-muted">{new Date(q.created_at).toLocaleString()}</div>
                  </div>
                </div>
                <p className="mt-4 rounded-xl bg-brand-soft p-4 text-sm text-brand-fg/85 leading-relaxed">{q.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
